//@flow
import React, {useEffect} from 'react';

import {
  Grid,
  IconButton,
  makeStyles,
  TableCell,
  Typography,
} from '@material-ui/core';
import CustomTextField from '../../../../../elements/CustomTextField/CustomTextField';
import {Edit} from '@material-ui/icons';
import CustomModal from '../../../../../elements/CustomModal/CustomModal';
import {useState} from 'react';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import {colors} from '../../../../../assets/styles/colors';
import {useStoreDomain} from '../../../../store/utils';
import {useProductContext} from '../../../context';
import {useProductMetadata} from '../../../../products/utils/hooks';
import {useForm, useWatch} from 'react-hook-form';
import {useAlert} from '../../../../../elements/Alert/AlertContext';

const useStyles = makeStyles(theme => ({
  textField: {
    width: '240px',

    '& input': {
      cursor: 'pointer',
    },
  },
  modalRoot: {
    display: 'flex',
    flexDirection: 'column',
    justifyItems: 'center',
    alignItems: 'center',
    textAlign: 'center',
    maxWidth: 600,
    width: '90vw',
    gap: '1.5rem',
    overflow: 'hidden',
  },
  title: {
    fontWeight: 600,
    fontSize: '1.375rem',
    color: colors.grey800,
  },
  content: {
    width: '100%',
    // marginBottom: '1.5rem',
  },
  submitButton: {
    maxWidth: '240px',
    fontSize: '1.125rem',
    fontWeight: 600,
  },
  contentSection: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(2),
    borderRadius: '12px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    padding: theme.spacing(0, 3, 3, 3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0, 2, 2, 2),
    },
    background: 'white',
  },
  sectionTitle: {
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    fontWeight: 600,
    marginTop: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(2),
    },
  },
  sectionDescription: {
    color: colors.grey700,
    fontSize: '0.875rem',
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  sectionCheckbox: {
    '& .MuiFormControlLabel-label': {
      color: colors.grey700,
      fontSize: '0.875rem',
    },
  },
  infoText: {
    color: colors.grey900,
    fontSize: 14,
  },
  wrapSeoExample: {
    pointerEvents: 'none',
    background: '#FFFFFF',
    border: '1px solid #E8E8ED',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    borderRadius: 8,
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(1),
  },
  colorBlue: {
    color: '#2E24A3',
    fontSize: 14,
    fontWeight: 500,
  },
  colorGreen: {
    fontSize: 12,
    fontWeight: 500,
    color: '#005B24',
  },
  colorGray: {
    fontSize: 12,
    fontWeight: 500,
    color: '#717190',
  },
  disableSection: {
    pointerEvents: 'none',
    opacity: 0.5,
  },
}));

export default function SeoCell() {
  const classes = useStyles();

  const {showAlert} = useAlert();

  const [product] = useProductContext(product => ({
    id: product.id,
    name: product.name,
    metaTitle: product.seoSetting && product.seoSetting.title,
  }));
  const {tiendadaDomain} = useStoreDomain();

  const {
    loading,
    productMetadata,
    getProductMetadata,
    editProductMetadata,
  } = useProductMetadata();

  const {register, errors, setValue, handleSubmit, control} = useForm();
  const [modalOpen, setModalOpen] = useState(false);

  const watchedTitle = useWatch({control: control, name: 'title'});
  const watchedDescription = useWatch({control: control, name: 'description'});

  useEffect(() => {
    if (!modalOpen || !product) return;
    getProductMetadata(product.id);
  }, [modalOpen, product, getProductMetadata]);

  useEffect(() => {
    if (!modalOpen) {
      setValue('title', '');
      setValue('description', '');
    }
  }, [modalOpen, setValue]);

  useEffect(() => {
    if (productMetadata) {
      setValue('title', productMetadata.title);
      setValue('description', productMetadata.description);
    }
  }, [productMetadata, setValue]);

  if (!product) return null;

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  const seoUrl = `https://${tiendadaDomain.domainName}/products/${
    product.id
  }-${encodeURI(product.name)}`;

  const onSubmit = data => {
    editProductMetadata(product.id, {
      enabled: true,
      title: data.title,
      description: data.description,
    })
      .then(() => {
        showAlert('Se ha guardado el SEO del producto', {
          type: 'success',
        });
        closeModal();
      })
      .catch(() => {
        showAlert('No se ha podido guardar el SEO del producto', {
          type: 'error',
        });
      });
  };

  const metaTitle = productMetadata ? productMetadata.title : product.metaTitle;

  return (
    <>
      <TableCell component="th" scope="row">
        <CustomTextField
          name="features"
          className={classes.textField}
          value={metaTitle ? `Título: ${metaTitle}` : ''}
          type="text"
          onFocus={() => {
            window.document.activeElement.blur();
            openModal();
          }}
          InputProps={{
            endAdornment: (
              <IconButton edge="end" onClick={openModal}>
                <Edit />
              </IconButton>
            ),
          }}
        />
      </TableCell>

      <CustomModal open={modalOpen} handleCloseModal={closeModal}>
        <aside className={classes.modalRoot}>
          <Typography className={classes.title} align="center">
            SEO de Producto
          </Typography>
          <div className={classes.content}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <div className={classes.wrapSeoExample}>
                  <Typography className={classes.colorBlue} align="left">
                    {watchedTitle ? watchedTitle : '--'}
                  </Typography>
                  <Typography className={classes.colorGreen} align="left">
                    {seoUrl}
                  </Typography>
                  <Typography className={classes.colorGray} align="left">
                    {watchedDescription ? watchedDescription : '--'}
                  </Typography>
                </div>
              </Grid>
              <Grid item xs={12}>
                <CustomTextField
                  maxLength={5}
                  label="Título"
                  name="title"
                  autoComplete="title"
                  type="text"
                  defaultValue={productMetadata ? productMetadata.title : ''}
                  error={!!errors.title}
                  helperText={errors.title ? errors.title.message : ''}
                  inputRef={register({
                    required: {
                      message: 'Campo requerido',
                      value: true,
                    },
                  })}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  className={classes.productInputField}
                />
              </Grid>
              <Grid item xs={12}>
                <CustomTextField
                  multiline
                  label="Descripción"
                  name="description"
                  defaultValue={
                    productMetadata ? productMetadata.description : ''
                  }
                  error={!!errors.description}
                  helperText={
                    errors.description ? errors.description.message : ''
                  }
                  inputRef={register({
                    required: {
                      message: 'Campo requerido',
                      value: true,
                    },
                  })}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  rows={4}
                  className={classes.productInputField}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography className={classes.infoText}>
                  Se recomienda utilizar menos de 80 caracteres para el título y
                  menos de 160 caracteres para la descripción.
                </Typography>
              </Grid>
            </Grid>
          </div>
          <CustomButton
            className={classes.submitButton}
            color="primary"
            variant="contained"
            disabled={loading}
            fullWidth
            onClick={handleSubmit(onSubmit)}
          >
            Guardar
          </CustomButton>
        </aside>
      </CustomModal>
    </>
  );
}
